
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { supabase, getAuthRedirectUrl } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      setError("Please enter your email address");
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      // Get the redirect URL for password reset
      const redirectTo = getAuthRedirectUrl('recovery');
      
      console.log('Using password reset redirect URL:', redirectTo);
      
      // Request password reset
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo
      });

      if (error) {
        console.error('Error requesting password reset:', error);
        setError(error.message);
        setIsProcessing(false);
        return;
      }

      // Request sent successfully
      setIsSuccess(true);
      toast.success("Password reset link sent! Check your email.");
      
    } catch (error: any) {
      console.error('Exception during password reset request:', error);
      setError(error.message || "An unexpected error occurred");
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Navbar />
      
      <div className="flex-1 flex items-center justify-center px-4 py-20">
        <div className="w-full max-w-md animate-fade-in">
          <h1 className="text-2xl font-semibold text-center mb-6">Reset Your Password</h1>
          
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {isSuccess ? (
            <Alert className="mb-6 border-green-200 bg-green-50 dark:border-green-900 dark:bg-green-950">
              <CheckCircle className="h-4 w-4 text-green-600 dark:text-green-400" />
              <AlertTitle>Check Your Email</AlertTitle>
              <AlertDescription className="space-y-4">
                <p>
                  We've sent a password reset link to <strong>{email}</strong>. 
                  Please check your inbox and spam folder.
                </p>
                <p>
                  The link will expire in 1 hour for security reasons.
                </p>
                <div className="pt-2">
                  <Button
                    type="button"
                    variant="default"
                    onClick={() => navigate('/sign-in')}
                    className="w-full"
                  >
                    Return to Sign In
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  disabled={isProcessing}
                  className="w-full"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full"
                disabled={isProcessing}
              >
                {isProcessing ? "Processing..." : "Send Reset Link"}
              </Button>
              
              <div className="text-center mt-4">
                <Button
                  type="button"
                  variant="link"
                  onClick={() => navigate('/sign-in')}
                  className="flex items-center gap-1 mx-auto"
                >
                  <ArrowLeft size={16} />
                  Back to Sign In
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default ForgotPassword;
